import { HttpClient, HttpHeaders, HttpResponse, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as LZString from 'lz-string';
import { Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CacheService } from './ cache.service';
import { GetFutureOrderTransactionParmas, GetOrderRequestParams } from '@app/types/app.types';
import { isChromeOnWindows } from '@app/utils/app.utils';

@Injectable({
  providedIn: 'root',
})
export class CoreApiService {
  private baseUrl: string = environment.baseUrl;

  cache = inject(CacheService);

  private readonly bottlerId = localStorage.getItem('bottler');
  private readonly CACHE_KEY = this.bottlerId ? `filters-${this.bottlerId}` : 'filters';
  private readonly TTL_MINUTES = 10;

  constructor(private http: HttpClient) {}

  getUserRoles(): Observable<Object> {
    return this.http.get(`${this.baseUrl}/user`);
  }

  getAzureProfile() {
    const headers = new HttpHeaders({
      accessToken: 'true',
    });
    return this.http.get(`https://graph.microsoft.com/v1.0/me/`, { headers });
  }

  // getLogOut() {
  //     // 1. Clear local storage or session storage of any authentication tokens
  //     this.authService.clearToken(); // Assuming your authentication service has a method to clear tokens

  //     // 2. Redirect to Azure AD logout endpoint
  //     const logoutUrl = `https://login.microsoftonline.com/${this.authService.getTenantId()}/oauth2/logout?post_logout_redirect_uri=${this.authService.getLogoutRedirectUri()}`;
  //     window.location.href = logoutUrl;

  // }

  getUserAvatarImage() {
    const headers = new HttpHeaders({
      accessToken: 'true',
    });
    return this.http.get(`https://graph.microsoft.com/v1.0/me/photo/$value`, {
      headers,
      responseType: 'arraybuffer',
    });
  }

  async getFilters(): Promise<Observable<any>> {
    if (isChromeOnWindows()) {
      let cached: string | null = await this.cache.get(this.CACHE_KEY);

      if (cached) {
        const decompressed = JSON.parse(LZString.decompress(cached!));
        return of(decompressed);
      }
    }

    return this.http.get<any>(`${this.baseUrl}/filter`).pipe(
      switchMap((res) => {
        if (isChromeOnWindows()) {
          this.cache.set(this.CACHE_KEY, res.data, this.TTL_MINUTES);
        }
        const decompressed = JSON.parse(LZString.decompress(res.data!));
        return of(decompressed);
      }),
    );
  }

  fetchOrderRequest(params: GetOrderRequestParams): Observable<Object> {
    return this.http.post(`${this.baseUrl}/order-request/`, params);
  }

  createOrderRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/order-request/create-request`, requestData);
  }

  createBulkOrderRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/order-request/create-bulk`, requestData);
  }

  createBulkOrderRequestWithProgress(requestData: any): Observable<HttpEvent<Object>> {
    return this.http.post(`${this.baseUrl}/order-request/create-bulk`, requestData, {
      reportProgress: true,
      observe: 'events' as const,
    });
  }

  editOrderRequest(requestData: any, requestId: any): Observable<Object> {
    return this.http.patch(`${this.baseUrl}/order-request/edit-request/${requestId}`, requestData);
  }

  deleteOrderRequest(requestId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/order-request/delete-request/${requestId}`);
  }

  deleteBulkOrderRequest(bulkGroupId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/order-request/delete-bulk-request/${bulkGroupId}`);
  }

  fetchFutureOrderTransaction(params: GetFutureOrderTransactionParmas): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/transaction`, { params });
  }

  fetchFutureOrderUsers(): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/request-user`);
  }

  fetchFutureOrderCustomers(params: any): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/customer`, { params });
  }

  postTransaction(payload: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/future-order/transaction`, payload);
  }

  fetchSaleRepresentativeList(params: any): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/sr-suggestion`, { params });
  }

  fetchDsmList(params: any): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/dsm-suggestion`, { params });
  }

  fetchSrUnderDsmList(params: any): Observable<Object> {
    return this.http.get(`${this.baseUrl}/future-order/dsm-sr-list`, { params });
  }

  addRemoveSalesRepresentatives(payload: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/future-order/save-hierarchy`, payload);
  }

  fetchEditHistory(requestId: string): Observable<Object> {
    return this.http.get(`${this.baseUrl}/order-request/edit-history/${requestId}`);
  }

  fetchOrderRequestInsights(requestId: string): Observable<object> {
    return this.http.get<any>(`${this.baseUrl}/order-request/insights/${requestId}`);
  }

  downloadOrderRequestInsights(requestId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/order-request/insights-download/${requestId}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  downloadBumpUpManual(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/order-request/manual-download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getMaterialMappingRequest(params: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/material-mapping/`, params);
  }

  createMaterialMappingRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/material-mapping/create-request`, requestData);
  }

  createBulkMaterialMappingRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/material-mapping/bulk-create`, requestData);
  }

  createBulkMaterialMappingRequestWithProgress(requestData: any): Observable<HttpEvent<Object>> {
    return this.http.post(`${this.baseUrl}/material-mapping/bulk-create`, requestData, {
      reportProgress: true,
      observe: 'events' as const,
    });
  }

  deleteMaterialMappingRequest(requestId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/material-mapping/delete-request/${requestId}`);
  }

  deleteBulkMaterialMappingRequest(bulkGroupId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/material-mapping/delete-bulk-request/${bulkGroupId}`);
  }

  editMaterialMappingRequest(requestData: any, requestId: any): Observable<Object> {
    return this.http.patch(`${this.baseUrl}/material-mapping/edit-request/${requestId}`, requestData);
  }

  fetchMaterialEditHistory(requestId: string): Observable<Object> {
    return this.http.get(`${this.baseUrl}/material-mapping/edit-history/${requestId}`);
  }

  fetchMaterialMappingColumnValues(params: any, routeName: string): Observable<Object> {
    return this.http.post(`${this.baseUrl}/${routeName}/column-values`, params);
  }

  getInnovationMappingRequest(params: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/innovation-prediction/`, params);
  }

  createInnovationMappingRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/innovation-prediction/create-request`, requestData);
  }

  editInnovationMappingRequest(requestData: any, requestId: any): Observable<Object> {
    return this.http.patch(`${this.baseUrl}/innovation-prediction/edit-request/${requestId}`, requestData);
  }

  deleteInnovationMappingRequest(requestId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/innovation-prediction/delete-request/${requestId}`);
  }

  fetchInnovationEditHistory(requestId: string): Observable<Object> {
    return this.http.get(`${this.baseUrl}/innovation-prediction/edit-history/${requestId}`);
  }

  createBulkInnovationRequest(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/innovation-prediction/bulk-create`, requestData);
  }

  createBulkInnovationRequestWithProgress(requestData: any): Observable<Object> {
    return this.http.post(`${this.baseUrl}/innovation-prediction/bulk-create`, requestData, {
      reportProgress: true,
      observe: 'events' as const,
    });
  }

  deleteBulkInnovationRequest(bulkGroupId: string): Observable<Object> {
    return this.http.delete(`${this.baseUrl}/innovation-prediction/delete-bulk-request/${bulkGroupId}`);
  }

  getInnovationAmlStatus(requestId: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}/innovation-prediction/aml-status/${requestId}`);
  }

  downloadInnovationAmlInvalid(requestId: string | number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/innovation-prediction/aml-invalid-download/${requestId}`, {
      responseType: 'blob',
    });
  }

  fetchInnovationInsights(requestId: string): Observable<object> {
    return this.http.get<any>(`${this.baseUrl}/innovation-prediction/insights/${requestId}`);
  }

  downloadInnovationInsights(requestId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/innovation-prediction/insights-download/${requestId}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }
}
